import React from 'react';
import { IllustrativeIcon, Txt } from '@vst/beam';
import { Alert } from '@vst/beam-icons/illustrative-icons';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { CustomDecorativeModal } from '@app/components/Modal';
import { useScreenResolution } from '@app/hooks';

import { ErrorEnum } from '../utils/types';

type BrowseErrorProps = {
  isModalOpen: boolean;
  refetchProducts: () => void;
  handleClose: (isSuccess: boolean, error?: ErrorEnum) => void;
};

const BrowseError: React.FC<BrowseErrorProps> = (props) => {
  const { isModalOpen, refetchProducts, handleClose } = props;
  const { t } = useTranslation(['BuyMore', 'Global']);

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const handleModalClose = React.useCallback(() => {
    handleClose(false, ErrorEnum.FETCH_PRODUCTS_ERROR);
  }, [handleClose]);

  const handleTryAgain = React.useCallback(() => {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_START,
      event_name: DataLayerEventName.FORM_START,
      event_label: AnalyticsEventNames.BuyMoreInitiated,
      event_category: Categories.BuyMore,
    });

    refetchProducts();
  }, [refetchProducts]);

  return (
    <CustomDecorativeModal
      header={t('Global.error.title')}
      isOpen={isModalOpen}
      data-cy="outcome-buy-more-browse-error"
      actions={{
        primary: {
          label: t('Browse.error.buttons.tryAgain'),
          onClick: handleTryAgain,
          'data-cy': 'buy-more-try-again-browse-error',
        },
        secondary: {
          label: t('Browse.error.buttons.close'),
          onClick: handleModalClose,
          'data-cy': 'buy-more-close-browse-error',
        },
      }}
      onClose={handleModalClose}
      icon={<IllustrativeIcon icon={Alert} color="teal_600" />}
    >
      <Txt color="subtle" variant={isMobile ? 'bodySmallRegular' : 'bodyLargeRegular'}>
        {t('Browse.error.description')}
      </Txt>
    </CustomDecorativeModal>
  );
};

export default BrowseError;
